import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import { ACCURACY_JOB_STATUS } from '../../generate-accuracy/constants';
import { useCanGenerateAccuracy } from '../../generate-accuracy/hooks/useCanGenerateAccuracy';
import { DISABLE_EDIT_FOR_INACTIVE_PROJECT_FF, PROJECT_STATUS_INACTIVE } from '../constants';

// TODO: Refactor to separate computational/behavior logic from presentation logic.
export const ActionsMenuCell = ({
  projectId,
  status,
  onChangeStatus,
  onEditProject,
  onGenerateAccuracy,
  onViewAccuracyDashboard,
  accuracyJobStatus,
  clearEditMode,
}: {
  projectId: string;
  status: string;
  onChangeStatus: (projectId: string) => void;
  onEditProject: (projectId: string) => void;
  onGenerateAccuracy: (projectId: string) => void;
  onViewAccuracyDashboard?: (projectId: string) => void;
  accuracyJobStatus?: string;
  clearEditMode: () => void;
}) => {
  const { formatMessage } = useIntl();
  const canGenerateAccuracy = useCanGenerateAccuracy();
  // TODO: cleanup with at_disable_edit_option_for_inactive_proj ff
  const isEditDisableForInactiveEnabled = Boolean(
    useFeatureFlagSplit(DISABLE_EDIT_FOR_INACTIVE_PROJECT_FF)
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isProjectInactive = status === PROJECT_STATUS_INACTIVE;
  // TODO: cleanup with at_disable_edit_option_for_inactive_proj ff
  const isEditProjectDisabled = isEditDisableForInactiveEnabled && isProjectInactive;

  // The dashboard only has data to show once the project's latest accuracy job has
  // completed; a missing, pending, in-progress, or failed job leaves nothing to open.
  const isViewAccuracyDashboardDisabled =
    isProjectInactive || accuracyJobStatus !== ACCURACY_JOB_STATUS.COMPLETED;

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    clearEditMode();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeStatusClick = () => {
    onChangeStatus(projectId);
    handleClose();
  };

  const handleEditProjectClick = () => {
    if (isEditProjectDisabled) return;
    onEditProject(projectId);
    handleClose();
  };

  const handleGenerateAccuracyClick = () => {
    onGenerateAccuracy(projectId);
    handleClose();
  };

  const handleViewAccuracyDashboardClick = () => {
    onViewAccuracyDashboard?.(projectId);
    handleClose();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleChangeStatusClick}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {formatMessage({ id: 'adminProjects.actionsMenu.changeStatus' })}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditProjectClick} disabled={isEditProjectDisabled}>
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {formatMessage({ id: 'adminProjects.actionsMenu.editProject' })}
          </ListItemText>
        </MenuItem>
        {canGenerateAccuracy && (
          // TODO: cleanup with cp_trigger_accuracy_from_ui ff
          <MenuItem onClick={handleGenerateAccuracyClick} disabled={isProjectInactive}>
            <ListItemIcon>
              <PlayCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {formatMessage({ id: 'adminProjects.actionsMenu.generateAccuracy' })}
            </ListItemText>
          </MenuItem>
        )}
        {canGenerateAccuracy && (
          // TODO: cleanup with cp_trigger_accuracy_from_ui ff
          <MenuItem
            onClick={handleViewAccuracyDashboardClick}
            disabled={isViewAccuracyDashboardDisabled}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {formatMessage({ id: 'adminProjects.actionsMenu.viewAccuracyDashboard' })}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
